import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import {
  filter, each, cloneDeep, reduce, map,
} from 'lodash';
import pluralize from 'pluralize';

import ReactHighcharts from 'react-highcharts';
import { Dropdown, MenuItem } from 'react-bootstrap';
import accounting from 'accounting';
import numeral from 'numeral';
import { format as formatMoney } from '../../../../components/money-display';
import ForecastForm from '../forecast-form';
import { getColorFromKey } from '../../../../util/helpers';
import { getHasNoDecisionSetup } from '../../../../components/search-form/search-form-reducer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import ProgressBar from '../../../../components/progress-bar';
import { getIsFontSizeBig } from '../../../App/AppReducer';

// Import Selectors
import { getOutcome } from '../../PerformanceReducer';
import { fetchForecast } from '../../PerformanceActions';

import styles from './styles.css';

function formatDays(input, suffix = '') {
  let num = input;
  let unit = 'day';
  if (input > 365) {
    num = input / 365;
    unit = 'year';
  } else if (input > 30) {
    num = input / 30;
    unit = 'month';
  }
  let out = accounting.formatNumber(num, 1);
  out = out.replace('.0', '');
  return `${out}${suffix} ${pluralize(unit, num)}`;
}

function createCategory(size, min, max) {
  const categories = [];
  if (size > 0) {
    // let c = min;
    let c = 0;
    while (c < max) {
      const next = Math.min(c + size, max);
      categories.push({
        label: '',
        altLabel: '',
        min: c,
        max: next,
      });
      c = next;
    }
  }

  each(categories, (category, i) => {
    const isMin = i === 0;
    const isMax = i === categories.length - 1;
    const minVal = formatDays(category.min);
    const maxVal = formatDays(category.max);
    if (isMin) {
      category.label = `0 - ${maxVal}`;
      category.altLabel = `less than ${maxVal}`;
    } else if (isMax) {
      category.label = `${formatDays(category.min, '+')}`;
      category.altLabel = `more than ${minVal}`;
    } else {
      category.label = `${minVal} - ${maxVal}`;
      category.altLabel = `between ${minVal} - ${maxVal}`;
    }
  });
  return categories;
}

function bucketize(unqualifiedStats, forecastKey) {
  const stats = filter(unqualifiedStats, (stat) => {
    const { owner } = stat;
    return owner && owner.is_active;
  });

  const results = { items: [] };
  // default values

  let mean = 0;
  let median = 0;
  let deviation = 0;
  let bucketSize = 0;
  let min = null;
  let max = 0;
  let winRate = 0;
  let totalForecast = 0;
  const total = stats.length;
  if (total > 0) {
    let totals = 0;
    let alls = [];
    each(stats, (stat) => {
      const tenures = stat.owner ? stat.owner.tenures || 0 : 0;
      alls.push(tenures);
      totals += tenures;
      max = Math.max(max, tenures);
      if (min === null) {
        min = tenures;
      } else {
        min = Math.min(tenures, min);
      }
      winRate += stat.stages.won.amountPercentage || 0;
      totalForecast += (stat[forecastKey] || 0) * 100;
    });

    alls = alls.sort((a, b) => (a - b > 0 ? 1 : -1));
    mean = totals / total;
    median = alls[Math.floor(alls.length / 2)];
    deviation = Math.sqrt(reduce(alls, (s, item) => s + ((item - mean) ** 2), 0) / total);
    bucketSize = Math.floor(deviation / 2);
  }

  each(stats, (stat) => {
    const cloneStat = cloneDeep(stat);
    const tenures = stat.owner ? stat.owner.tenures || 0 : 0;
    cloneStat.bucket = bucketSize !== 0 ? Math.floor(tenures / bucketSize) : null;
    results.items.push(cloneStat);
  });


  return {
    ...results,
    mean,
    median,
    deviation,
    bucketSize,
    chartCategories: createCategory(bucketSize, min, max),
    averageWinRate: total !== 0 ? winRate / total : 0,
    averageForecast: total !== 0 ? totalForecast / total : 0,
    min,
    max,
  };
}
function getRateLabel(stage) {
  let prefix = stage;
  if (stage === 'won') {
    prefix = 'Win';
  } else if (stage === 'lost') {
    prefix = 'Loss';
  } else if (stage === 'noDecision') {
    prefix = 'No Decision';
  }
  return `${prefix} Rate`;
}
function getCountLabel(stage) {
  let prefix = stage;
  if (stage === 'won') {
    prefix = 'Win';
  } else if (stage === 'lost') {
    prefix = 'Loss';
  } else if (stage === 'noDecision') {
    prefix = 'No Decision';
  }
  return `${prefix} Count`;
}
function getAmountLabel(stage) {
  let prefix = stage;
  if (stage === 'won') {
    prefix = 'Amount Won';
  } else if (stage === 'lost') {
    prefix = 'Amount Lost';
  } else if (stage === 'noDecision') {
    prefix = 'No Decision Amount';
  }
  return prefix;
}

const USER_PREFERENCE_REPORT_TYPE = 'tenure-distribution-report-type';
const USER_PREFERENCE_FORECAST_CATEGORY = 'tenure-distribution-forecast-category';
const USER_PREFERENCE_PERIOD = 'tenure-distribution-forecast-period';

class SalesTenureGraph extends Component {
  constructor(props) {
    super(props);
    this.state = {
      type: 'amount',
      period: null,
      buckets: null,
      // loadingForecast: props.displayForecastData, // init
      loadingForecast: false,
      forecastCategoryParams: null,
    };
    this.setReportType = this.setReportType.bind(this);
    this.handleForecastParamChange = this.handleForecastParamChange.bind(this);
  }

  componentWillMount() {
    const { userPreferences } = this.context;
    const type = userPreferences[USER_PREFERENCE_REPORT_TYPE] || 'amount';
    this.setState({
      type,
    });
    this.checkState(this.props.outcome);
  }

  componentWillReceiveProps(nextProps) {
    this.checkState(nextProps.outcome);
  }

  setReportType(type) {
    this.setState({ type });
    this.context.updateUserPreference(USER_PREFERENCE_REPORT_TYPE, type);
  }

  checkState(outcome) {
    const buckets = bucketize(outcome, `${this.props.forecastKey}Forecast`);
    this.setState({ buckets });
  }

  handleForecastParamChange(period, forecastCategoryParams) {
    this.setState({ period, forecastCategoryParams });
    const { criteria, minForecastDate, maxForecastDate } = this.props;
    const params = {
      ...criteria,
      selectedPeriod: period,
      forecast: true,
      period: [minForecastDate, maxForecastDate],
    };
    this.setState({ loadingForecast: true });
    this.props.dispatch(fetchForecast(params, forecastCategoryParams, 'tenure'))
      .then(
        () => {
          this.setState({ loadingForecast: false });
        },
        () => {
          this.setState({ loadingForecast: false });
        },
      );
  }

  renderGraph() {
    const { organization, hasNoDecisionSetup, isFontSizeBig } = this.props;
    const { buckets } = this.state;
    const forecastKey = `${this.props.forecastKey}Forecast`;

    if (!buckets || buckets.items.length === 0) {
      return null;
    }
    const { type } = this.state;
    const categories = buckets.chartCategories;

    let suffix = '';
    let symbol = '';
    if (organization) {
      suffix = organization.currency_suffix;
      symbol = organization.currency_symbol;
    }
    const series = [];
    const grand = {};
    const groupedData = reduce(buckets.items, (items, item) => {
      if (item.total) {
        const { bucket } = item;
        if (!items[bucket]) {
          items[bucket] = {
            items: [],
            stats: {
              won: {
                sum: 0, count: 0, average: 0, averageGap: 0, total: 0, totalGap: 0,
              },
              lost: {
                sum: 0, count: 0, average: 0, averageGap: 0, total: 0, totalGap: 0,
              },
              forecast: {
                sum: 0, count: 0, average: 0, averageGap: 0, total: 0, totalGap: 0,
              },
              ...(hasNoDecisionSetup ? {
                noDecision: {
                  sum: 0, count: 0, average: 0, averageGap: 0, total: 0, totalGap: 0,
                },
              } : {}),
            },
          };
        }
        each(item.stages, (stats, stage) => {
          if (items[bucket].stats[stage]) {
            const sum = stats[`${type}Percentage`] || 0;
            const count = stats.total || 0;
            const total = type === 'amount' ? (stats.amount || 0) : (stats.total || 0);
            items[bucket].stats[stage].sum += sum;
            items[bucket].stats[stage].total += total;
            items[bucket].stats[stage].count += count;
            if (!grand[stage]) {
              grand[stage] = {
                sum: 0, count: 0, average: 0, total: 0, totalAverage: 0,
              };
            }
            grand[stage].sum += sum;
            grand[stage].total += total;
            grand[stage].count += count;
          }
        });

        if (!grand.forecast) {
          grand.forecast = {
            sum: 0, count: 0, average: 0, total: 0, totalAverage: 0,
          };
        }
        const tenureForecast = (item[forecastKey] || 0) * 100;
        items[bucket].stats.forecast.sum += tenureForecast;
        items[bucket].stats.forecast.count += 1;
        grand.forecast.sum += tenureForecast;
        grand.forecast.count += 1;

        items[bucket].items.push(item);
      }
      return items;
    }, {});

    each(grand, (g) => {
      const val = g.sum || 0;
      const count = buckets.items.length || 0;
      g.average = count !== 0 ? val / count : 0;
      g.totalAverage = count !== 0 ? (g.total || 0) / count : 0;
    });

    each(groupedData, (group) => {
      each(group.stats, (stats, stat) => {
        const val = stats.sum || 0;
        // const total = stats.count || 0;
        const total = group.items.length || 0;
        stats.average = total !== 0 ? val / total : 0;
        /*
        stats.averageGap = stats.average - grand[stat].average;
        stats.totalGap = stats.total - grand[stat].totalAverage;
        */
        /* since https://github.com/lumen-software/TruSales/issues/703#issuecomment-386128539 */
        stats.averageGap = grand[stat].average !== 0 ? ((stats.average - grand[stat].average) / grand[stat].average) * 100 : 0;
        stats.totalGap = grand[stat].totalAverage !== 0 ? ((stats.total - grand[stat].totalAverage) / grand[stat].totalAverage) * 100 : 0;
      });
    });
    const data = [];
    each(categories, (category, k) => {
      if (groupedData[k]) {
        const y = groupedData[k].items.length || 0;
        const ownerIds = map(groupedData[k].items, 'owner_id');

        data.push({
          y,
          salesReps: numeral(y).format('0'),
          tenures: numeral(y).format('0'),
          stats: groupedData[k].stats,
          ownerIds,
          events: {
            click: (event) => {
              const { period, forecastCategoryParams } = this.state;
              this.props.onChartSegmentSelect(event.point.options.ownerIds, period, forecastCategoryParams);
            },
          },
        });
      } else {
        data.push({
          y: 0,
          salesReps: '0',
          tenures: '0',
          stats: null,
        });
      }
    });

    series.push({
      name: 'Sales Tenures',
      altName: 'Sales Tenures',
      color: '#0c7cd5',
      mean: accounting.formatNumber(buckets.mean, 1),
      median: accounting.formatNumber(buckets.median, 1),
      pointWidth: 10,
      data,
      fillOpacity: 0.05,
      marker: {
        fillColor: '#ffffff',
        lineWidth: 2,
        radius: 5,
        lineColor: null, // inherit from series
      },
    });

    const config = {
      chart: {
        plotBackgroundColor: null,
        plotBorderWidth: null,
        plotShadow: false,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        type: 'areaspline',
        height: isFontSizeBig ? 600 : 400,
      },
      credits: {
        enabled: false,
      },
      title: {
        text: null,
      },
      tooltip: {
        formatter: function fmt() {
          const header = `<thead>
            <tr>
              <th colspan="3" style="color: ${this.series.color};">
                ${this.series.options.altName}<br /> ${this.key.altLabel}
              </th>
            </tr>
          </thead>`;

          let body = `
            <tr>
              <td>Number of reps:</td>
              <td class="text-right" colspan="2">${this.point.options.salesReps}</td>
            </tr>
          `;
          let tfoot = '';
          if (this.point.options.stats) {
            body += `
              <tr>
                <td></td>
                <th class="text-right">Avg</th>
                <th class="text-right">Gap</th>
              </tr>
            `;
            each(this.point.options.stats, (stat, stage) => {
              const isForecast = stage === 'forecast';
              if (!isForecast) {
                const rateVal = accounting.formatNumber(stat.average, 1);
                const rateGap = accounting.formatNumber(stat.averageGap, 1);
                let countVal = stat.total;
                const countGap = accounting.formatNumber(stat.totalGap, 1);
                if (type === 'amount') {
                  countVal = formatMoney(countVal, true, 1, symbol, suffix);
                } else {
                  countVal = accounting.formatNumber(countVal, 0);
                }
                const color = getColorFromKey(stage);
                const label = type === 'amount' ? getAmountLabel(stage) : getCountLabel(stage);
                let rateCls = '';
                let ratePrefix = '';
                const reverse = stage !== 'won';
                if (stat.averageGap > 0) {
                  rateCls = reverse ? 'label label-danger' : 'label label-success';
                  ratePrefix = '+';
                } else if (stat.averageGap < 0) {
                  rateCls = reverse ? 'label label-success' : 'label label-danger';
                }
                let countCls = '';
                let countPrefix = '';
                if (stat.totalGap > 0) {
                  countCls = reverse ? 'label label-danger' : 'label label-success';
                  countPrefix = '+';
                } else if (stat.totalGap < 0) {
                  countCls = reverse ? 'label label-success' : 'label label-danger';
                }

                body += `
                  <tr>
                    <td style="color: ${color};">${getRateLabel(stage)}</td>
                    <th class="text-right" style="padding-right: 5px;">${rateVal}%</th>
                    <th class="text-right"><span class="${rateCls}">${ratePrefix}${rateGap}%</span></th>
                  </tr>
                  <tr>
                    <td style="color: ${color};">${label}</td>
                    <th class="text-right" style="padding-right: 5px;">${countVal}</th>
                    <th class="text-right"><span class="${countCls}">${countPrefix}${countGap}%</span></th>
                  </tr>
                `;
              }
            });
            if (this.point.options.stats.forecast) {
              const forecastAverage = accounting.formatNumber(this.point.options.stats.forecast.average, 1);
              const forecastGap = accounting.formatNumber(this.point.options.stats.forecast.averageGap, 1);
              let forecastCls = '';
              let forecastPrefix = '';
              if (this.point.options.stats.forecast.averageGap > 0) {
                forecastCls = 'label label-success';
                forecastPrefix = '+';
              } else if (this.point.options.stats.forecast.averageGap < 0) {
                forecastCls = 'label label-danger';
              }
              body += `
                <tr>
                  <td style="">Forecast Accuracy</td>
                  <th class="text-right" style="padding-right: 5px;">${forecastAverage}%</th>
                  <th class="text-right"><span class="${forecastCls}">${forecastPrefix}${forecastGap}%</span></th>
                </tr>
              `;
            }
            tfoot = `
              <tfoot><tr><td colspan="3">
                Gap is the +/- percentage size of the gap compared<br />
                to the average for all reps in the data set
              </td></tr></tfoot>
            `;
          }


          return `<table class="table table-condensed ${styles.tipTable}" style="margin: 0;">${header}<tbody>${body}</tbody>${tfoot}</table>`;
        },
        useHTML: true,
        backgroundColor: 'rgba(0,0,0, 0.75)',
        borderColor: 'rgba(0,0,0, 0.95)',
        style: {
          color: 'rgba(255, 255, 255, 0.75)',
        },
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        categories,
        alternateGridColor: 'rgba(66,66,66,0.1)',
        crosshair: {
          width: 1,
        },
        lineWidth: 0,
        labels: {
          style: {
            color: 'rgba(0, 0, 0, .5)',
            fontSize: isFontSizeBig ? '16px' : null,
          },
          formatter: function fx() {
            return this.value.label;
          },
        },
        tickColor: 'rgba(66,66,66,0.5)',
      },
      yAxis: {
        allowDecimals: false,
        title: {
          text: 'Active Reps',
          style: {
            color: 'rgba(0, 0, 0, 0.8)',
            fontWeight: 'bold',
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
        labels: {
          style: {
            color: 'rgba(0, 0, 0, .5)',
            fontSize: isFontSizeBig ? '16px' : null,
          },
        },
        gridLineColor: 'rgba(66,66,66,0.5)',
        tickColor: 'rgba(66,66,66,0.5)',
      },
      series,
      plotOptions: {
        column: {
          borderWidth: 0,
        },
      },
    };
    const w = isFontSizeBig ? 180 : 120;
    return (
      <div className="animated fadeIn">
        <ReactHighcharts config={config} />
        <div className="row">
          <div className="col-md-8 col-md-push-2 col-lg-6 col-lg-push-3">
            <div className="row">
              <div className="col-xs-6">
                <table className={`table table-condensed table-borderless ${styles.legend}`}>
                  <tbody>
                    <tr>
                      <th width={w}><nobr>Average Tenure:</nobr></th>
                      <td><nobr>{formatDays(buckets.mean)}</nobr></td>
                    </tr>
                    <tr>
                      <th><nobr>Median Tenure:</nobr></th>
                      <td><nobr>{formatDays(buckets.median)}</nobr></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="col-xs-6">
                <table className={`table table-condensed table-borderless ${styles.legend}`}>
                  <tbody>
                    <tr>
                      <th width={w}><nobr>Average Win Rate:</nobr></th>
                      <td><nobr>{accounting.formatNumber(buckets.averageWinRate, 1)}%</nobr></td>
                    </tr>
                    <tr>
                      <th><nobr>Average Accuracy:</nobr></th>
                      <td><nobr>{accounting.formatNumber(buckets.averageForecast, 1)}%</nobr></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderMenu() {
    const { forecastClosedMonths } = this.props;
    const { type } = this.state;
    return (
      <h5 className="sub-header-with-controls">
        <span>Sales tenure distribution with</span>
        &nbsp;
        <Dropdown
          id="tenure-count-dropdown"
        >
          <Dropdown.Toggle bsSize="small">
            {type}
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <MenuItem
              active={type === 'amount'}
              onSelect={() => { this.setReportType('amount'); }}
            >
              Amount
            </MenuItem>
            <MenuItem
              active={type === 'count'}
              onSelect={() => { this.setReportType('count'); }}
            >
              Count
            </MenuItem>
          </Dropdown.Menu>
        </Dropdown>
        &nbsp;outcome rates
        {false && <ForecastForm
          disabled={this.state.loadingForecast}
          onParamChange={this.handleForecastParamChange}
          userPreferenceKey={USER_PREFERENCE_FORECAST_CATEGORY}
          userPreferencePeriodKey={USER_PREFERENCE_PERIOD}
          rememberControlStates
          forecastClosedMonths={forecastClosedMonths}
        />}
      </h5>
    );
  }

  render() {
    const { displayForecastData } = this.props;
    const { loadingForecast } = this.state;
    const loading = loadingForecast && displayForecastData;

    return (
      <div className="animated fadeIn">
        <div>
          {this.renderMenu()}
          {!loading && this.renderGraph()}
          {loading && <div><ProgressBar /></div>}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    outcome: getOutcome(state),
    hasNoDecisionSetup: getHasNoDecisionSetup(state),
    organization: getUserCurrentOrganization(state),
    isFontSizeBig: getIsFontSizeBig(state),
  };
}

SalesTenureGraph.propTypes = {
  dispatch: PropTypes.func.isRequired,
  outcome: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hasNoDecisionSetup: PropTypes.bool.isRequired,
  organization: PropTypes.shape({}).isRequired,
  forecastClosedMonths: PropTypes.number.isRequired,
  minForecastDate: PropTypes.string,
  maxForecastDate: PropTypes.string,
  criteria: PropTypes.shape({}),
  onChartSegmentSelect: PropTypes.func,
  displayForecastData: PropTypes.bool,
  isFontSizeBig: PropTypes.bool.isRequired,
  forecastKey: PropTypes.string,
};
SalesTenureGraph.defaultProps = {
  criteria: null,
  minForecastDate: null,
  maxForecastDate: null,
  onChartSegmentSelect: () => {},
  displayForecastData: false,
  forecastKey: 'forecast',
};

SalesTenureGraph.contextTypes = {
  updateUserPreference: PropTypes.func.isRequired,
  userPreferences: PropTypes.object.isRequired,
};
export default connect(mapStateToProps)(SalesTenureGraph);
